import { RefObject, useCallback, useEffect, useState } from 'react';
import { openOverlaysOf } from 'flow/components/nav/list-keys/list-row-focus';
import useKeyboardShortcuts from 'flow/hooks/useKeyboardShortcuts';
import { useAppSelector } from 'flow/hooks/useRedux';
import { selectLoggedInUserIsInCall } from 'flow/selectors/call-selectors';
import {
    ContactKey,
    ExtensionKey,
    ThirdPartyContactKey,
} from 'flow/types/EntityKeys';

// The open dialogs. A HeadlessUI dialog is in the page only while it is open,
// and a native <dialog> has the open attribute while it is.
const DIALOG = '[role="dialog"], [role="alertdialog"], dialog[open]';

// An open dialog covers the rows behind it, and their highlight with them. A
// row in the dialog, such as in the picker's list, is not behind it.
const isBehindDialog = (row: Element) =>
    !row.closest(DIALOG) && document.querySelector(DIALOG) !== null;

const rowOverlayIsOpen = (row: Element) => {
    const rowItem = row.closest('li');
    return rowItem !== null && openOverlaysOf(rowItem).length > 0;
};

// A contact list row's transfer shortcuts. Only the highlighted row listens,
// during a call. A row behind an open dialog leaves the keys to the dialog,
// so one key press sends one transfer. The row's transfer dialog closes when
// the row shows another contact.
const useTransferShortcuts = (
    entityKey: ExtensionKey | ContactKey | ThirdPartyContactKey,
    selected: boolean,
    rowRef: RefObject<Element>,
) => {
    const { callActionByGroup } = useKeyboardShortcuts();
    const loggedInUserIsInCall = useAppSelector(selectLoggedInUserIsInCall);

    const [transferDialogIsOpen, setTransferDialogIsOpen] = useState(false);
    const [transferDialogEntityKey, setTransferDialogEntityKey] =
        useState(entityKey);
    if (transferDialogEntityKey !== entityKey) {
        setTransferDialogEntityKey(entityKey);
        setTransferDialogIsOpen(false);
    }

    useEffect(() => {
        if (!loggedInUserIsInCall || !selected) {
            return undefined;
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            // A row leaves the page a moment before its listener goes.
            const row = rowRef.current;
            // While the row's menu is open, the user works in it, so the
            // keys act on no contact. The row keeps the highlight meanwhile
            // (useListTabStop), so no other contact takes the keys.
            if (
                !row?.isConnected ||
                isBehindDialog(row) ||
                rowOverlayIsOpen(row)
            ) {
                return;
            }
            callActionByGroup('telephony', event, {
                entityKey,
                setTransferSelectionDialogOpen: setTransferDialogIsOpen,
            });
        };
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [loggedInUserIsInCall, selected, entityKey, rowRef, callActionByGroup]);

    const closeTransferDialog = useCallback(
        () => setTransferDialogIsOpen(false),
        [],
    );

    // Focus goes into the dialog, so the highlight leaves the row. The dialog
    // stays this contact's until it closes.
    return {
        transferDialogIsShown: transferDialogIsOpen,
        closeTransferDialog,
    };
};

export default useTransferShortcuts;
